import { ChangeDetectionStrategy, Component, ViewChild } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Subject } from "rxjs";
import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { ICellRendererAngularComp } from "@bfx/ag-grid-angular";
import LoggerService from "@bfx/logger";
import { OverlayPanel } from "@bfx/primeng/overlaypanel";
import {
  FormattedValueDateTotal,
  ShowValueDateTotalsEvent,
} from "app/bfx-bmark-summary/blotters/spot-risk/bmark-spot-risk-blotter.component";
import { BmarkRiskInstrumentRendererComponent } from "app/bfx-bmark-summary/blotters/spot-risk/renderers/bmark-risk-instrument-renderer.component";
import { ZERO } from "shared/base-components/number-spinner.component";
import { ValueDateTotalInfo } from "shared/cubiq-services/lookup/model/impl/bmrisk/SpotRiskModel";
import { BmarkBaseRiskDisplayModel } from "shared/cubiq-services/model/benchmarks/BmarkSpotRiskDisplay.model";
const logger = LoggerService.for(__dirname);

/**
 * Renderer for Instrument in benchmark Spot Risk blotter
 */
@Component({
  selector: "spot-risk-instrument",
  templateUrl: "./bmark-spot-risk-instrument.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BmarkSpotRiskInstrumentRendererComponent
  extends BmarkRiskInstrumentRendererComponent
  implements ICellRendererAngularComp
{
  @ViewChild("op") overlay: OverlayPanel;

  /**
   * BmarkSpotRiskInstrumentRendererComponent is immutable' after agInit() due to refresh() returning false
   *
   * Change from ag-grid will destroy component and create a new one
   *
   * A ChangeDetectorRef push (markForCheck) must never be called
   */
  constructor(protected translator: TranslateService) {
    super(translator);
  }
  /**
   * Show the error popover
   * @param $event
   */
  public showError($event): void {
    this.popoutInfo = this.errorInfo;
    this.overlay.show($event);
  }

  public hide(): void {
    this.overlay.hide();
  }

  /**
   *  Show the value dates popover - this will always create the tooltip form latest
   * aggregate models available
   * @param $event
   */
  public showValueDates($event: { target: any }): void {
    const pairModel: BmarkBaseRiskDisplayModel = this.params?.data;
    if (pairModel) {
      this.notifyShowValueDateTotals($event, pairModel);
    }
  }

  /**
   * CReate a popout model for the renderer overlay that contains all the
   * info for the value date totals for the instrument
   *
   * @param pairModel
   * @returns
   */
  private notifyShowValueDateTotals(
    event: { target: any },
    pairModel: BmarkBaseRiskDisplayModel,
  ): void {
    const vdInfos: ValueDateTotalInfo[] = pairModel.getValueDataTotalInfos();

    if (vdInfos == null || vdInfos.length == 0) {
      logger.out.info(`bmrkr-c ${pairModel.instrument} - no orders found`);
      return;
    }

    const retRows: FormattedValueDateTotal[] = [];

    const displayInMillions = pairModel.isMaterial
      ? false
      : pairModel.notation in CubiqTS.PositionUnit &&
        pairModel.notation == CubiqTS.PositionUnit.Million;
    const materialSuffix = pairModel.isMaterial ? "Oz" : "";
    for (const total of vdInfos) {
      let intentStr: string;
      let amount: CubiqTS.BigNumber;
      if (total.netAmount.isGreaterThan(ZERO)) {
        intentStr = "BUY";
        amount = total.netAmount;
      } else {
        intentStr = "SELL";
        amount = total.netAmount.abs();
      }
      if (displayInMillions) {
        amount = amount.dividedBy(1e6);
      }
      const amountStr = amount.toFormat(pairModel.precision) + materialSuffix;
      // let row = `${intentStr} ${amountStr}${materialSuffix} (${total.valueDateStr})`;
      retRows.push({ intent: intentStr, amount: amountStr, valueDate: total.valueDateStr });
    }
    const valueDateSubject: Subject<ShowValueDateTotalsEvent> =
      this.params?.context.valueDateSubject;
    if (!valueDateSubject) {
      logger.out.warn("bmrkr-c - CAnnot find value date subject");
    } else {
      const showEvent = {
        valueDateTotals: retRows,
        pairName: pairModel.instrument,
        fixingDate: "Fixing Orders",
        triggerEvent: event,
      };
      valueDateSubject.next(showEvent);
    }
  }
}
